import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import MainService from '../../services/MainService';

const PAGE_SIZE = 20;

const todayInput = () => new Date().toISOString().split('T')[0];

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const parseTotal = (response, fallback = 0) => Number(response?.Total ?? response?.total ?? fallback) || 0;

const dateParts = (value) => {
  const date = value ? new Date(value) : new Date();
  return {
    FromDay: date.getDate(),
    FromMonth: date.getMonth() + 1,
    FromYear: date.getFullYear(),
  };
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB');
};

const formatAmount = (value) => Number(value || 0).toFixed(2);

const OrderFollowupPage = ({ title, endpoint }) => {
  const [rows, setRows] = useState([]);
  const [areas, setAreas] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filter, setFilter] = useState({
    Date: todayInput(),
    Keyword: '',
    AreaId: '',
    DriverId: '',
    StatusId: '',
  });
  const [appliedFilter, setAppliedFilter] = useState(filter);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const loadLookups = useCallback(async () => {
    try {
      const [areaResponse, driverResponse, statusResponse] = await Promise.all([
        MainService.AreasInDropDown(),
        MainService.DriversInDropDown(),
        MainService.StatusInDropDown(),
      ]);
      setAreas(parseList(areaResponse));
      setDrivers(parseList(driverResponse));
      setStatuses(parseList(statusResponse));
    } catch (err) {
      console.error('Load followup lookups error:', err);
      setError('Failed to load filters');
    }
  }, []);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService[endpoint]({
        ...dateParts(appliedFilter.Date),
        AreaId: Number(appliedFilter.AreaId) || 0,
        DriverId: Number(appliedFilter.DriverId) || 0,
        StatusId: Number(appliedFilter.StatusId) || 0,
        Keyword: appliedFilter.Keyword?.trim() || '',
        Skip: (currentPage - 1) * PAGE_SIZE,
        Take: PAGE_SIZE,
      });
      const list = parseList(response);
      setRows(list);
      setTotalCount(parseTotal(response, list.length));
    } catch (err) {
      console.error(`${endpoint} error:`, err);
      setRows([]);
      setTotalCount(0);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  }, [appliedFilter, currentPage, endpoint]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const updateFilter = (field, value) => {
    setFilter((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilter = () => {
    setAppliedFilter(filter);
    setCurrentPage(1);
  };

  const refresh = () => {
    const next = { Date: todayInput(), Keyword: '', AreaId: '', DriverId: '', StatusId: '' };
    setFilter(next);
    setAppliedFilter(next);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 border-b border-gray-200 pb-4">
          <h1 className="text-2xl font-bold text-gray-900">{title} | {formatDate(appliedFilter.Date)}</h1>
          <p className="mt-1 text-sm text-gray-500">Filter followup orders by date, keyword, area, driver, and status.</p>
        </div>

        {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
          <FilterInput label="Date" type="date" value={filter.Date} onChange={(value) => updateFilter('Date', value)} />
          <FilterInput label="Keyword" value={filter.Keyword} onChange={(value) => updateFilter('Keyword', value)} onEnter={applyFilter} />
          <FilterSelect label="Area" value={filter.AreaId} onChange={(value) => updateFilter('AreaId', value)} options={areas} />
          <FilterSelect label="Driver" value={filter.DriverId} onChange={(value) => updateFilter('DriverId', value)} options={drivers} />
          <FilterSelect label="Status" value={filter.StatusId} onChange={(value) => updateFilter('StatusId', value)} options={statuses} />
        </div>

        <div className="mt-5 flex justify-center gap-3">
          <button
            type="button"
            onClick={applyFilter}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
          >
            <Search className="h-4 w-4" />
            Filter
          </button>
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title} List</h2>
            <p className="text-sm text-gray-500">Showing {rows.length} of {totalCount} orders</p>
          </div>
        </div>

        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[1700px] divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>#</Th>
                <Th>Voucher No</Th>
                <Th>Client Bill</Th>
                <Th>Driver</Th>
                <Th>Delivery Date</Th>
                <Th>Product Price</Th>
                <Th>Delivery Charge</Th>
                <Th>Tax</Th>
                <Th>Customer Amount</Th>
                <Th>Client Amount</Th>
                <Th>Client</Th>
                <Th>Client Mob</Th>
                <Th>Client Emirate</Th>
                <Th>Client Location</Th>
                <Th>Customer</Th>
                <Th>Customer Mob</Th>
                <Th>Customer Emirate</Th>
                <Th>Customer Location</Th>
                <Th>Attempts</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <EmptyRow colSpan={20} text="Loading followup orders..." />
              ) : rows.length === 0 ? (
                <EmptyRow colSpan={20} text="No orders found." />
              ) : (
                rows.map((row, index) => (
                  <tr key={`${row.Id}-${row.VoucherNo}`} className="hover:bg-gray-50">
                    <Td>{(currentPage - 1) * PAGE_SIZE + index + 1}</Td>
                    <Td strong>{row.VoucherNo || '-'}</Td>
                    <Td>{row.ClientBillNo || '-'}</Td>
                    <Td>
                      <div>{row.DriverName || '-'}</div>
                      <div className="text-xs text-gray-500">{row.DriverMob || ''}</div>
                    </Td>
                    <Td>{formatDate(row.DeliveryDate)}</Td>
                    <Td>{formatAmount(row.ProductPrice)}</Td>
                    <Td>{formatAmount(row.DeliveryCharge)}</Td>
                    <Td>{formatAmount(row.TAX)}</Td>
                    <Td>{formatAmount(row.CustomerAmount)}</Td>
                    <Td>{formatAmount(row.ClientAmount)}</Td>
                    <Td>{row.Client?.Name || '-'}</Td>
                    <Td>{row.Client?.MobileNo1 || '-'}</Td>
                    <Td>{row.ClientAddress?.Emirate?.Name || '-'}</Td>
                    <Td>{row.ClientAddress?.Location?.Name || '-'}</Td>
                    <Td>{row.Customer?.Name || '-'}</Td>
                    <Td>{row.Customer?.MobileNo1 || '-'}</Td>
                    <Td>{row.CustomerAddress?.Emirate?.Name || '-'}</Td>
                    <Td>{row.CustomerAddress?.Location?.Name || '-'}</Td>
                    <Td>{row.Attempts ?? 0}</Td>
                    <Td>{row.Status?.Name || '-'}</Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <TableFooter currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} rowCount={rows.length} loading={loading} onPageChange={setCurrentPage} />
      </div>
    </div>
  );
};

const FilterInput = ({ label, value, onChange, type = 'text', onEnter }) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && onEnter) onEnter();
      }}
      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
    />
  </div>
);

const FilterSelect = ({ label, value, onChange, options }) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
    >
      <option value="">All</option>
      {options.map((option) => (
        <option key={option.Id} value={option.Id}>
          {option.Name}
        </option>
      ))}
    </select>
  </div>
);

const Th = ({ children }) => <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">{children}</th>;
const Td = ({ children, strong = false }) => <td className={`whitespace-nowrap px-4 py-3 text-sm ${strong ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{children}</td>;
const EmptyRow = ({ colSpan, text }) => <tr><td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-gray-500">{text}</td></tr>;

const TableFooter = ({ currentPage, totalPages, totalCount, rowCount, loading, onPageChange }) => (
  <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
    <span>
      Showing {rowCount ? (currentPage - 1) * PAGE_SIZE + 1 : 0} to {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}
    </span>
    <div className="flex gap-2">
      <button type="button" onClick={() => onPageChange((page) => Math.max(1, page - 1))} disabled={currentPage <= 1 || loading} className="rounded-lg border border-gray-300 px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">Previous</button>
      <span className="px-3 py-1.5">Page {currentPage} of {totalPages}</span>
      <button type="button" onClick={() => onPageChange((page) => Math.min(totalPages, page + 1))} disabled={currentPage >= totalPages || loading} className="rounded-lg border border-gray-300 px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">Next</button>
    </div>
  </div>
);

export default OrderFollowupPage;
